import React, { useEffect, useId, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ADMIN_PROSPECT_SELECTION_ALL_MATCHING,
  ADMIN_PROSPECT_SELECTION_EXPLICIT,
  buildAdminProspectSelectionDescriptor,
} from '../../services/admin/prospectSelection.js';
import { describeAdminProspectTargetingChips } from '../../services/admin/prospectTargetingFilters.js';
import {
  ADMIN_PROSPECT_CAMPAIGN_NAME_MAX,
  createAdminProspectCampaign,
  createAdminProspectCampaignCreateAttempt,
} from '../../services/admin/prospectCampaignsService.js';
import { listActiveAdminProspectLetterTemplates } from '../../services/admin/prospectLetterTemplatesService.js';
import {
  adminProspectCampaignErrorKey,
  adminProspectCampaignSelectionModeLabel,
  classifyAdminCallableError,
} from './adminProspectCampaignDisplay.js';

/**
 * Create campaign from an existing Lot 6.7B selection descriptor.
 * Does not mass-fetch IDs for all_matching.
 *
 * One logical create attempt keeps the same operationId + payload across
 * transient/ambiguous retries for the open dialog. Close/reopen resets.
 */
export default function AdminCreateProspectCampaignDialog({
  open,
  t,
  selection,
  selectedCount,
  onClose,
}) {
  const titleId = useId();
  const errorId = useId();
  const nameRef = useRef(null);
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [attemptFrozen, setAttemptFrozen] = useState(false);
  const [templatesStatus, setTemplatesStatus] = useState('idle');
  const [activeTemplates, setActiveTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const submitLockRef = useRef(false);
  const createAttemptRef = useRef(createAdminProspectCampaignCreateAttempt());

  useEffect(() => {
    if (!open) return;
    setName('');
    setSaving(false);
    setError('');
    setAttemptFrozen(false);
    setTemplatesStatus('loading');
    setActiveTemplates([]);
    setSelectedTemplateId('');
    submitLockRef.current = false;
    createAttemptRef.current.reset();

    let cancelled = false;
    listActiveAdminProspectLetterTemplates()
      .then((rows) => {
        if (cancelled) return;
        setActiveTemplates(rows);
        if (rows.length === 1) {
          setSelectedTemplateId(rows[0].templateId);
        }
        setTemplatesStatus('ready');
      })
      .catch(() => {
        if (cancelled) return;
        setActiveTemplates([]);
        setTemplatesStatus('error');
      });

    const id = window.setTimeout(() => nameRef.current?.focus(), 0);
    return () => {
      cancelled = true;
      window.clearTimeout(id);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !saving) {
        event.preventDefault();
        onClose?.();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, saving, onClose]);

  if (!open) return null;

  const descriptor = buildAdminProspectSelectionDescriptor(selection);
  const modeLabel = adminProspectCampaignSelectionModeLabel(descriptor.mode, t);
  const targetingChips =
    descriptor.mode === ADMIN_PROSPECT_SELECTION_ALL_MATCHING
      ? describeAdminProspectTargetingChips(descriptor.targeting, t)
      : [];
  const nameReady = name.trim().length > 0 && name.trim().length <= ADMIN_PROSPECT_CAMPAIGN_NAME_MAX;
  const selectedTemplate = activeTemplates.find((row) => row.templateId === selectedTemplateId) || null;
  const hasActiveTemplate = activeTemplates.length > 0;
  const templateReady =
    hasActiveTemplate &&
    selectedTemplate != null &&
    Number.isInteger(selectedTemplate.currentVersion);
  const canSubmit = nameReady && selectedCount > 0 && templateReady && !saving && templatesStatus !== 'loading';

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canSubmit || submitLockRef.current || !selectedTemplate) return;
    submitLockRef.current = true;
    setSaving(true);
    setError('');

    const letterTemplate = {
      templateId: selectedTemplate.templateId,
      version: selectedTemplate.currentVersion,
    };

    // First submit freezes operationId + exact payload; retries reuse them.
    const payload = createAttemptRef.current.resolveAttemptPayload({
      name: name.trim(),
      selection: descriptor,
      letterTemplate,
    });
    setAttemptFrozen(true);

    try {
      const result = await createAdminProspectCampaign(payload);
      const campaignId = result?.campaignId;
      if (!campaignId) {
        setError(t(adminProspectCampaignErrorKey('internal')));
        setSaving(false);
        submitLockRef.current = false;
        return;
      }
      // Includes idempotent returns (e.g. prior create whose build later failed).
      onClose?.();
      navigate(`/admin/prospection/campaigns/${campaignId}`);
    } catch (err) {
      setError(t(adminProspectCampaignErrorKey(classifyAdminCallableError(err))));
      setSaving(false);
      submitLockRef.current = false;
    }
  };

  const fieldClass =
    'mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white p-5 shadow-lg">
        <h2 id={titleId} className="text-lg font-semibold text-slate-900">
          {t('admin.prospection.campaign.create.title')}
        </h2>
        <form
          onSubmit={handleSubmit}
          className="mt-4 space-y-4"
          noValidate
          aria-describedby={error ? errorId : undefined}
        >
          <label className="block text-sm font-medium text-slate-700" htmlFor="admin-campaign-name">
            {t('admin.prospection.campaign.create.name')}
            <input
              id="admin-campaign-name"
              ref={nameRef}
              type="text"
              value={name}
              maxLength={ADMIN_PROSPECT_CAMPAIGN_NAME_MAX}
              onChange={(event) => setName(event.target.value)}
              disabled={saving || attemptFrozen}
              autoComplete="off"
              className={fieldClass}
              required
            />
          </label>

          <div>
            <label className="block text-sm font-medium text-slate-700" htmlFor="admin-campaign-template">
              {t('admin.prospection.campaign.create.letterTemplate')}
            </label>
            {templatesStatus === 'loading' ? (
              <p className="mt-1 text-sm text-slate-500" role="status">
                {t('admin.prospection.campaign.create.letterTemplateLoading')}
              </p>
            ) : null}
            {templatesStatus === 'error' ? (
              <p className="mt-1 text-sm text-rose-700" role="alert">
                {t('admin.prospection.campaign.create.letterTemplateLoadError')}
              </p>
            ) : null}
            {templatesStatus === 'ready' && hasActiveTemplate ? (
              <select
                id="admin-campaign-template"
                value={selectedTemplateId}
                onChange={(event) => setSelectedTemplateId(event.target.value)}
                disabled={saving || attemptFrozen || activeTemplates.length <= 1}
                className={fieldClass}
                required
              >
                {activeTemplates.length > 1 ? (
                  <option value="">{t('admin.prospection.campaign.create.letterTemplatePlaceholder')}</option>
                ) : null}
                {activeTemplates.map((row) => (
                  <option key={row.templateId} value={row.templateId}>
                    {t('admin.prospection.campaign.create.letterTemplateOption')
                      .replace('{name}', row.name || row.templateId)
                      .replace('{version}', String(row.currentVersion))}
                  </option>
                ))}
              </select>
            ) : null}
            {templatesStatus === 'ready' && !hasActiveTemplate ? (
              <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950">
                <p>{t('admin.prospection.campaign.create.letterTemplateRequired')}</p>
                <Link
                  to="/admin/prospection/letter-templates/new"
                  className="mt-2 inline-block font-semibold text-indigo-700 underline"
                >
                  {t('admin.prospection.campaign.create.letterTemplateCreateLink')}
                </Link>
              </div>
            ) : null}
          </div>

          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
            <p>
              <span className="font-medium text-slate-900">
                {t('admin.prospection.campaign.create.selectedCount').replace(
                  '{count}',
                  String(selectedCount),
                )}
              </span>
            </p>
            <p className="mt-1">
              {t('admin.prospection.campaign.create.selectionType')}: {modeLabel}
            </p>
            {descriptor.mode === ADMIN_PROSPECT_SELECTION_EXPLICIT ? null : (
              <div className="mt-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  {t('admin.prospection.campaign.create.targetingSummary')}
                </p>
                {targetingChips.length > 0 ? (
                  <ul className="mt-1 flex flex-wrap gap-1.5">
                    {targetingChips.map((chip) => (
                      <li
                        key={chip.id}
                        className="rounded-full bg-white px-2.5 py-0.5 text-xs font-medium text-slate-700 ring-1 ring-slate-200"
                      >
                        {chip.label}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="mt-1 text-xs text-slate-500">
                    {t('admin.prospection.campaign.create.targetingEmpty')}
                  </p>
                )}
              </div>
            )}
          </div>

          {error ? (
            <p id={errorId} className="text-sm text-rose-700" role="alert">
              {error}
            </p>
          ) : null}

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={() => onClose?.()}
              disabled={saving}
              className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
            >
              {t('admin.prospection.campaign.create.cancel')}
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
            >
              {saving
                ? t('admin.prospection.campaign.create.saving')
                : t('admin.prospection.campaign.create.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
